import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { RiSendPlane2Line, RiLoader4Line, RiMicLine, RiCalendarLine, RiInformationLine } from "react-icons/ri";
import { useQueryClient } from "@tanstack/react-query";
import StatusBadge from "./StatusBadge";
import { submitQualitativeProject, toggleProjectToPause } from "../qualSurveysActions";
import { useGlobalContext } from "../../../Contexts/GlobalContext";

const SUBMITTABLE_STATUSES = ["draft", "changes_requested", "paused"];
const PAUSABLE_STATUSES = ["submitted_for_review", "live"];
export default function DetailHeader({ data }) {
    const { user, setSnackbar } = useGlobalContext();
    const projectID = data?._id;
    const organizationID = user?.organization?._id;
    const queryClient = useQueryClient();

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);


    const status = data?.status;
    const isSubmittable = SUBMITTABLE_STATUSES.includes(status);
    const isPauseable = PAUSABLE_STATUSES.includes(status);
    const handleSubmit = async () => {
        if (!isSubmittable || !organizationID || !projectID) return;
        setSubmitting(true);
        setSubmitError(null);
        const result = await submitQualitativeProject({ organizationID, projectID });
        console.log("submit result=>", result);

        if (result.success) {
            await queryClient.invalidateQueries({ queryKey: ["projectDetails", organizationID, projectID] });
        } else {
            setSubmitError(result.message || "Submission failed. Please try again.");
        }
        setSubmitting(false);
    };

    // Format creation date
    const createdAt = data?.createdAt
        ? `${new Date(data.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}, ${new Date(data.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
        : null;

    const totalParticipants = data?.participants?.numberOfParticipants || 0;
    const feasibility = data?.feasibilityAndModeration?.feasibilityCount || 0;
    const feasibilityWindow = data?.feasibilityAndModeration?.feasibilityWindow || {};
    const feasibilityStatusColor = feasibility >= totalParticipants ? "green-600" : "red-600";
    const feasibilityStatusText = feasibility >= totalParticipants ? "Feasible" : "Low Feasibility";
    const { moderationFees = 0, currency } =
        data?.feasibilityAndModeration || {};
    const feasibilityText = `${feasibility} Participants / ${feasibilityWindow?.frequency} ${feasibilityWindow?.unit?.label || ""} / ${moderationFees} ${currency?.value} per participant`;


    const handleTogglePauseProject = async () => {
        try {
            setSubmitting(true);
            if (!isPauseable) {
                setSnackbar({
                    open: true,
                    msg: "Project is not pausable",
                    severity: "error",
                });
                return;
            };
            const result = await toggleProjectToPause({ organizationID, projectID });
            if (!result.success) {
                throw new Error("Unable to pause project!");
            }
            await queryClient.invalidateQueries({ queryKey: ["projectDetails", organizationID, projectID] });
            setSnackbar({
                open: true,
                msg: "Project paused successfully",
                severity: "success",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                msg: error?.message || "Unable to pause project!",
                severity: "error",
            });
        } finally {
            setSubmitting(false);
        }



    }
    return (
        <div className="w-full bg-white border border-[#E0E0E0] rounded-md p-5 space-y-6">
            <div className="w-full flex items-start justify-between flex-wrap gap-4">
                {/* Left: title + metadata */}
                <div className="flex items-start gap-5 flex-wrap flex-1 min-w-0">
                    {/* Icon */}
                    {/* <div className="w-11 h-11 bg-[#0226BE]/8 rounded-xl flex items-center justify-center flex-shrink-0">
                        <RiMicLine size={20} className="text-[#0226BE]" />
                    </div> */}

                    <div className="min-w-0 flex-1">
                        {/* Title row */}
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                            <Tooltip title={data?.projectInfo?.internalProjectTitle || ""}>
                                <h1 className="text-[#0226BE] text-[24px] font-[700] max-w-[500px]
                                    text-ellipsis overflow-hidden whitespace-nowrap cursor-default
                                    hover:text-[#021ea8] transition-colors leading-tight">
                                    {data?.projectInfo?.internalProjectTitle || "—"}
                                </h1>
                            </Tooltip>

                            {/* Status badge */}
                            {status && <StatusBadge status={status} />}

                            {/* Project type chip */}
                            <span className="text-[11px] font-bold text-[#0226BE] bg-[#0226BE]/10
                                px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                                {data?.researchActivity?.moderatedSessionType?.toUpperCase() || "Qualitative"}
                            </span>
                        </div>

                        {/* Meta row */}
                        <div className="flex items-center gap-5 flex-wrap">
                            {/* Project ID */}
                            <div className="flex flex-col gap-0.5">
                                <p className="text-[#999] text-[10px] font-[500] uppercase tracking-widest">
                                    Project No.
                                </p>
                                <p className="text-[#333] text-[13px] font-[700]">
                                    PN#{data?.projectID || data?._id?.slice(-6)?.toUpperCase() || "—"}
                                </p>
                            </div>

                            {/* Divider */}
                            <div className="h-8 w-px bg-[#E0E0E0]" />

                            {/* Session type */}
                            {data?.researchActivity?.sessionMethod && (
                                <div className="flex flex-col gap-0.5">
                                    <p className="text-[#999] text-[10px] font-[500] uppercase tracking-widest">
                                        Session
                                    </p>
                                    <p className="text-[#333] text-[13px] font-[600] capitalize">
                                        {data.researchActivity.sessionMethod.replace(/_/g, " ")}
                                    </p>
                                </div>
                            )}

                            {/* Divider */}
                            {data?.researchActivity?.sessionMethod && (
                                <div className="h-8 w-px bg-[#E0E0E0]" />
                            )}

                            {/* Created date */}
                            {createdAt && (
                                <div className="flex flex-col gap-0.5">
                                    <p className="text-[#999] text-[10px] font-[500] uppercase tracking-widest">
                                        Creation Date
                                    </p>
                                    <p className="flex text-[#333] text-[13px] font-[600] capitalize items-center gap-1">
                                        <RiCalendarLine size={13} />
                                        {createdAt}
                                    </p>

                                </div>
                            )}

                            {/* Participants */}
                            {!!totalParticipants && (
                                <>
                                    <div className="h-8 w-px bg-[#E0E0E0]" />
                                    <div className="flex flex-col gap-0.5">
                                        <p className="text-[#999] text-[10px] font-[500] uppercase tracking-widest">
                                            Participants
                                        </p>
                                        <p className="text-[#333] text-[13px] font-[600]">
                                            {data.participants.numberOfParticipants}
                                        </p>
                                    </div>
                                </>
                            )}
                            {/* feasibility */}
                            {!!feasibility && (
                                <>
                                    <div className="h-8 w-px bg-[#E0E0E0]" />
                                    <Tooltip title={feasibilityStatusText} arrow>
                                        <div className="flex flex-col gap-0.5">
                                            <p className="text-[#999] text-[10px] font-[500] uppercase tracking-widest">
                                                Feasibility
                                            </p>
                                            <p className={`text-${feasibilityStatusColor} text-[13px] font-[600]`}>
                                                {feasibilityText}
                                            </p>
                                        </div>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: actions */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {isSubmittable && (
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="flex items-center gap-2 bg-[#0226BE] hover:bg-[#021ea8] disabled:opacity-50
                                text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
                                shadow-sm shadow-[#0226BE]/20 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <><RiLoader4Line size={15} className="animate-spin" /> Submitting…</>
                            ) : (
                                <><RiSendPlane2Line size={15} /> Submit for Review</>
                            )}
                        </button>
                    )}
                    {!!isPauseable && (
                        <button
                            onClick={handleTogglePauseProject}
                            disabled={submitting}
                            className="flex items-center gap-2 bg-[#0226BE] hover:bg-[#021ea8] disabled:opacity-50
                                text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
                                shadow-sm shadow-[#0226BE]/20 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Pausing..." : "Pause Project"}
                        </button>
                    )}
                    {submitError && (
                        <p className="text-xs text-red-500 max-w-[220px] text-right">{submitError}</p>
                    )}

                    {/* Changes requested banner */}
                    {/* {status === "changes_requested" && data?.changeRequests?.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-[260px]">
                            <p className="text-xs font-semibold text-amber-700 mb-0.5">Changes Requested</p>
                            <p className="text-xs text-amber-600 leading-relaxed line-clamp-2">
                                {data?.changeRequests[data.changeRequests?.length - 1]?.message}
                            </p>
                        </div>
                    )} */}
                </div>
            </div>
            {!!isPauseable && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-4  flex items-center gap-2 text-amber-700 text-sm">
                    <RiInformationLine size={20} />
                    <p className=" font-semibold  mb-0.5">This project is currently under review. To edit the project details, please change its status to Paused.</p>

                </div>
            )}
        </div>
    );
}